import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { Activity, AlertTriangle, Calendar, DollarSign, TrendingDown } from 'lucide-react';

import { useGetDashboardDataByWorkspace } from '@/api/dashboard/useDashboardDetailsByWorkspace';
import SignalsLoader from '@/component-library/primitives/Loaders/SignalsLoader';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ChurnSignal } from '@/models/DashboardData';

import ChurnSignalModal from '../../../DashboardPage/Components/ChurnSignalModal';

import { extractChurnSignalsFromDashboard, filterSignalsForMember } from './memberProfileChurnUtils';

interface MemberSignalsTabProps {
  memberData: MemberOverview | undefined;
}

const MemberSignalsTab: React.FC<MemberSignalsTabProps> = ({ memberData }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSignal, setSelectedSignal] = useState<ChurnSignal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { startTime, endTime } = useMemo(() => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return { startTime: startDate.toISOString(), endTime: endDate.toISOString() };
  }, []);

  const { data: dashboardData, isLoading } = useGetDashboardDataByWorkspace(startTime, endTime);

  const memberSignals = useMemo(() => {
    const all = extractChurnSignalsFromDashboard(dashboardData);
    return filterSignalsForMember(all, memberData?.id, memberData?.email);
  }, [dashboardData, memberData?.id, memberData?.email]);

  const filteredSignals = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return memberSignals;
    return memberSignals.filter(
      (signal) =>
        signal.title.toLowerCase().includes(q) ||
        signal.description.toLowerCase().includes(q) ||
        signal.signalType.toLowerCase().includes(q),
    );
  }, [memberSignals, searchTerm]);

  const columnHelper = createColumnHelper<ChurnSignal>();

  const getSignalTypeIcon = (signalType: string) => {
    switch (signalType) {
      case 'high_risk_churn':
        return <AlertTriangle className="w-4 h-4 text-custom-red" />;
      case 'medium_risk_churn':
        return <AlertTriangle className="w-4 h-4 text-custom-yellow" />;
      case 'low_activity':
        return <TrendingDown className="w-4 h-4 text-custom-yellow" />;
      case 'payment_failure':
        return <DollarSign className="w-4 h-4 text-custom-red" />;
      case 'subscription_downgrade':
        return <TrendingDown className="w-4 h-4 text-custom-yellow" />;
      default:
        return <Activity className="w-4 h-4 text-custom-darkSilver" />;
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const columns = [
    columnHelper.accessor('title', {
      header: () => <div className="text-custom-black font-medium">{t('MemberProfilePage.signalColumnLabel', 'Signal')}</div>,
      cell: (info) => (
        <div
          className="flex items-center cursor-pointer p-2 -m-2 rounded"
          onClick={() => {
            setSelectedSignal(info.row.original);
            setIsModalOpen(true);
          }}
        >
          {getSignalTypeIcon(info.row.original.signalType)}
          <div className="ml-3">
            <div className="text-sm font-medium text-custom-black hover:text-custom-blue hover:underline">{info.renderValue()}</div>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('priority', {
      header: () => <div className="text-custom-black font-medium">{t('MemberProfilePage.priorityColumnLabel', 'Priority')}</div>,
      cell: (info) => {
        const priority = (info.renderValue() || 'low') as string;
        const getPriorityColor = (level: string) => {
          switch (level.toLowerCase()) {
            case 'critical':
            case 'high':
              return 'bg-custom-red/10 text-custom-red border-custom-red/20';
            case 'medium':
              return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
            case 'low':
              return 'bg-custom-green/10 text-custom-green border-custom-green/20';
            default:
              return 'bg-custom-blue/10 text-custom-blue border-custom-blue/20';
          }
        };
        return (
          <span className={`text-xs px-2 py-1 rounded-md ${getPriorityColor(priority)}`}>{priority.toLowerCase()}</span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: () => <div className="text-custom-black font-medium">{t('MemberProfilePage.statusColumnLabel', 'Status')}</div>,
      cell: (info) => {
        const status = (info.renderValue() || 'new') as string;
        const getStatusColor = (level: string) => {
          switch (level.toLowerCase()) {
            case 'new':
              return 'text-custom-blue';
            case 'in_progress':
              return 'text-custom-yellow';
            case 'resolved':
              return 'text-custom-green';
            default:
              return 'text-custom-darkSilver';
          }
        };
        return (
          <span className={`font-medium ${getStatusColor(status)}`}>{status.replace('_', ' ').toLowerCase()}</span>
        );
      },
    }),
    columnHelper.accessor('riskScore', {
      header: () => <div className="text-custom-black font-medium">{t('MemberProfilePage.riskScoreColumnLabel', 'Risk')}</div>,
      cell: (info) => {
        const score = info.renderValue() || 0;
        const getScoreColor = (s: number) => {
          if (s >= 60) return 'text-custom-red';
          if (s >= 30) return 'text-custom-yellow';
          return 'text-custom-green';
        };
        return <span className={`font-medium ${getScoreColor(Number(score))}`}>{score}%</span>;
      },
    }),
    columnHelper.accessor('createdAt', {
      header: () => <div className="text-custom-black font-medium">{t('MemberProfilePage.createdColumnLabel', 'Created')}</div>,
      cell: (info) => (
        <div className="flex items-center text-sm text-custom-darkSilver">
          <Calendar className="w-4 h-4 mr-1" />
          {formatDate(info.renderValue() || '')}
        </div>
      ),
    }),
  ];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSignal(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 max-w-md">
        <SearchField initialValue={searchTerm} setSearchValue={setSearchTerm} withFilter={false} />
      </div>
      {isLoading ? (
        <SignalsLoader />
      ) : filteredSignals.length > 0 ? (
        <Table columns={columns} data={filteredSignals} />
      ) : (
        <NoDataBoard
          description={
            memberSignals.length === 0
              ? t('MemberProfilePage.memberSignalsEmpty', 'No churn signals for this person in the last 30 days.')
              : t('MemberProfilePage.memberSignalsNoSearch', 'No signals match your search.')
          }
        />
      )}
      <ChurnSignalModal isOpen={isModalOpen} onClose={handleCloseModal} signal={selectedSignal} />
    </div>
  );
};

export default MemberSignalsTab;
